import React, { useState, useRef, useContext, useCallback, useMemo, useEffect } from "react";
import "./Components/SellCar.css";
import { MyContext } from "../Context/MyContext.jsx";
import LocationMap from "../Components/Map/LocationMap";
import CarLoader from "../Components/Loading/CarLoader";
import { FormPageSkeleton } from "../Components/Skeleton/Skeleton";
import PageLoadGate from "../Components/Skeleton/PageLoadGate";
import { useToast } from "../Components/Toast/ToastProvider";
import { listingApi, getAccessToken } from "../../services/api";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { carToFormPrefill, parseListingLocation } from "../../Utils/listingFormUtils";

const filesToBase64 = (files) =>
  Promise.all(
    files.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        })
    )
  );

const formatMessage = (template, vars = {}) =>
  Object.entries(vars).reduce(
    (msg, [key, value]) => msg.replace(new RegExp(`\\{${key}\\}`, "g"), value),
    template || ""
  );

const clearDependentFields = (fieldName, formFields, data) => {
  const next = { ...data };
  formFields.forEach((field) => {
    if (field.dependsOn?.field === fieldName) {
      next[field.name] = "";
      Object.assign(next, clearDependentFields(field.name, formFields, next));
    }
  });
  return next;
};

export default function SellCar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams();
  const isEditing = Boolean(editId);
  const context = useContext(MyContext);
  const { sellcar_form, sellcar_page, currentUser } = context;

  const { showToast } = useToast();
  const routes = sellcar_page.routes || {};
  const validation = sellcar_page.validation || {};
  const errors = sellcar_page.errors || {};
  const imageSection = sellcar_page.imageSection || {};
  const actions = sellcar_page.actions || {};
  const pageTitle = isEditing ? "Edit Your Listing" : sellcar_page.title;
  const pageSubtitle = isEditing
    ? "Update your vehicle details and save changes"
    : sellcar_page.subtitle;

  const [selectedRole, setSelectedRole] = useState(isEditing ? "owner" : null);
  const [formData, setFormData] = useState(() => location.state?.prefill || {});
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [previewImages, setPreviewImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEditing);
  const [fieldErrors, setFieldErrors] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isEditing) return undefined;

    let cancelled = false;

    const loadListing = async () => {
      if (!getAccessToken()) {
        navigate(routes.login || "/login");
        return;
      }

      setLoadingEdit(true);
      try {
        let car = location.state?.editCar;
        const matchesEdit =
          car &&
          [car._id, car.id, car.legacyId]
            .filter(Boolean)
            .some((value) => String(value) === String(editId));

        if (!matchesEdit) {
          const res = await listingApi.get(editId);
          car = res.data;
        }

        if (cancelled || !car) return;

        setFormData(carToFormPrefill(car, context, currentUser));
        setSelectedRole("owner");
        const images = (car.images || []).filter((img) => img && String(img).trim());
        setExistingImages(images);
        setPreviewImages(images.length ? images : []);
        setSelectedLocation(parseListingLocation(car.location));
      } catch (err) {
        if (!cancelled) {
          showToast({
            type: "error",
            title: "Could not load listing",
            message: err.message || "Listing not found",
          });
          navigate(routes.afterSubmit || "/profile");
        }
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    };

    loadListing();

    return () => {
      cancelled = true;
    };
  }, [isEditing, editId, location.state?.editCar, currentUser, navigate, showToast, routes.afterSubmit, routes.login]);

  const showRoleStep = sellcar_page.role === true && !isEditing;

  const visibleFields = useMemo(
    () => (sellcar_form || []).filter((field) => field.is_show !== false),
    [sellcar_form]
  );

  const getSourceItems = useCallback(
    (sourceKey) => {
      if (!sourceKey) return [];
      const items = context[sourceKey];
      return Array.isArray(items) ? items : [];
    },
    [context]
  );

  const getSelectOptions = useCallback(
    (field) => {
      if (field.options?.length) {
        return field.options.map((opt) =>
          typeof opt === "string" ? { value: opt, label: opt } : opt
        );
      }

      const labelKey = field.optionLabel || "name";
      const valueKey = field.optionValue || "name";
      const sourceItems = getSourceItems(field.optionsSource);

      if (!field.dependsOn) {
        return sourceItems.map((item) => ({
          value: item[valueKey],
          label: item[labelKey],
          id: item.id,
        }));
      }

      const { field: parentField, parentSource, filterKey } = field.dependsOn;
      const parentValue = formData[parentField];
      if (!parentValue) return [];

      const parentItems = getSourceItems(parentSource);
      const parentItem = parentItems.find((item) => item.name === parentValue);
      if (!parentItem) return [];

      return sourceItems
        .filter((item) => Number(item[filterKey]) === Number(parentItem.id))
        .map((item) => ({
          value: item[valueKey],
          label: item[labelKey],
          id: item.id,
        }));
    },
    [formData, getSourceItems]
  );

  const getSelectPlaceholder = useCallback(
    (field) => {
      const selectMessages = sellcar_page.selectMessages || {};

      if (field.dependsOn) {
        const parentValue = formData[field.dependsOn.field];
        if (!parentValue && field.dependsOn.waitMessageKey) {
          return selectMessages[field.dependsOn.waitMessageKey] || "";
        }
      }

      if (field.placeholder) return field.placeholder;

      return formatMessage(selectMessages.default || "Select {label}", {
        label: field.label,
      });
    },
    [formData, sellcar_page.selectMessages]
  );

  const isSelectDisabled = useCallback(
    (field) => Boolean(field.dependsOn && !formData[field.dependsOn.field]),
    [formData]
  );

  const handleLocationChange = useCallback((location) => {
    setSelectedLocation(location);
    setFieldErrors((prev) => ({ ...prev, location: undefined }));
  }, []);

  const handleChange = (name, value) => {
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      return clearDependentFields(name, visibleFields, next);
    });
  };

  const validateForm = () => {
    const formErrors = {};

    visibleFields.forEach((field) => {
      const value = formData[field.name];
      const isEmpty =
        value === undefined || value === null || String(value).trim() === "";

      if (field.required && isEmpty) {
        formErrors[field.name] = formatMessage(validation.fieldRequired, {
          label: field.label,
        });
        return;
      }

      if (!isEmpty && field.type === "number") {
        const numVal = Number(value);
        if (!Number.isNaN(numVal)) {
          if (field.min !== undefined && numVal < field.min) {
            formErrors[field.name] = formatMessage(validation.minValue, {
              label: field.label,
              min: field.min,
            });
          } else if (field.max !== undefined && numVal > field.max) {
            formErrors[field.name] = formatMessage(validation.maxValue, {
              label: field.label,
              max: field.max,
            });
          }
        }
      }
    });

    if (sellcar_page.map && sellcar_page.map.is_show !== false && !selectedLocation?.lat) {
      const hasAddress = Boolean(selectedLocation?.address || formData.location);
      if (!isEditing || !hasAddress) {
        formErrors.location = validation.locationRequired;
      }
    }

    return formErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!getAccessToken()) {
      showToast({
        type: "error",
        title: errors.loginRequiredTitle,
        message: errors.loginRequiredMessage,
      });
      navigate(routes.login || "/login");
      return;
    }

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setFieldErrors(formErrors);
      showToast({
        type: "error",
        title: validation.failedTitle,
        message: validation.requiredMessage,
      });
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);

    try {
      const newImages = imageFiles.length ? await filesToBase64(imageFiles) : [];
      const images = [...existingImages, ...newImages];
      const payload = {
        role: selectedRole,
        ...formData,
        location: selectedLocation,
        images,
        status: "awaiting",
      };

      if (isEditing) {
        await listingApi.update(editId, payload);
        window.dispatchEvent(new CustomEvent("carsinfinite:bootstrap-reload"));
        showToast({
          type: "success",
          title: "Listing updated",
          message: "Your changes have been saved and sent for review",
        });
      } else {
        await listingApi.submit(payload);
        showToast({
          type: "success",
          title: validation.successTitle,
          message: validation.successMessage,
        });
      }
      navigate(routes.afterSubmit || "/profile");
    } catch (err) {
      showToast({
        type: "error",
        title: errors.submissionFailedTitle,
        message: err.message || errors.submissionFailedMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDraft = async () => {
    if (!getAccessToken()) {
      navigate(routes.login || "/login");
      return;
    }

    setIsSubmitting(true);
    try {
      const newImages = imageFiles.length ? await filesToBase64(imageFiles) : [];
      const images = [...existingImages, ...newImages];
      const payload = {
        role: selectedRole,
        ...formData,
        location: selectedLocation,
        images,
        status: "draft",
      };

      if (isEditing) {
        await listingApi.update(editId, payload);
        window.dispatchEvent(new CustomEvent("carsinfinite:bootstrap-reload"));
      } else {
        await listingApi.submit(payload);
      }
      showToast({
        type: "warning",
        title: validation.draftTitle,
        message: validation.draftMessage,
      });
    } catch (err) {
      showToast({
        type: "error",
        title: errors.draftFailedTitle,
        message: err.message || errors.draftFailedMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = (files) => {
    const fileArr = Array.from(files);
    setImageFiles((prev) => [...prev, ...fileArr]);
    setPreviewImages((prev) => [
      ...prev,
      ...fileArr.map((file) => URL.createObjectURL(file)),
    ]);
  };

  const inputClass = (name) => (fieldErrors[name] ? "input-error" : "");

  const renderField = (field) => {
    if (field.type === "file") return null;

    switch (field.type) {
      case "text":
      case "tel":
      case "number":
      case "url":
        return (
          <input
            type={field.type === "url" ? "url" : field.type}
            placeholder={field.placeholder || field.label}
            value={formData[field.name] || ""}
            className={inputClass(field.name)}
            min={field.type === "number" && field.min !== undefined ? field.min : undefined}
            max={field.type === "number" && field.max !== undefined ? field.max : undefined}
            step={field.type === "number" && field.step !== undefined ? field.step : undefined}
            onChange={(e) => handleChange(field.name, e.target.value)}
          />
        );

      case "select": {
        const options = getSelectOptions(field);
        return (
          <select
            value={formData[field.name] || ""}
            className={inputClass(field.name)}
            onChange={(e) => handleChange(field.name, e.target.value)}
            disabled={isSelectDisabled(field)}
          >
            <option value="">{getSelectPlaceholder(field)}</option>
            {options.map((opt) => (
              <option key={opt.id ?? opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }

      default:
        return null;
    }
  };

  const roleStep = showRoleStep && !selectedRole;

  if (loadingEdit) {
    return (
      <div className="sellcar">
        <FormPageSkeleton />
      </div>
    );
  }

  const pageContent = roleStep ? (
    <div className="sellcar">
      <div className="role-select">
        <div className="role-box content-card">
          <div className="card-header role-card-header">
            <div className="header-badge">{sellcar_page.title}</div>
            <h2 className="role-title">{sellcar_page.roleSection.title}</h2>
            <p className="role-subtitle">{sellcar_page.roleSection.subtitle}</p>
          </div>

          <div className="role-options">
            {sellcar_page.roles.map((role) => (
              <button
                type="button"
                key={role.id}
                className="role-card"
                onClick={() => setSelectedRole(role.value)}
              >
                <div className="role-icon">{role.icon}</div>
                <div className="role-info">
                  <h3>{role.name}</h3>
                  <p>{role.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className="sellcar">
      {isSubmitting && (
        <CarLoader
          title={actions.submittingTitle}
          message={actions.submittingMessage}
        />
      )}

      <div className="sellcar-page">
        <div className="form-wrapper">
          <form className="form-container content-card" onSubmit={handleSubmit} noValidate>
            <div className="sell-layout">
              <div className="image-preview-section">
                <input
                  type="file"
                  multiple={imageSection.multiple !== false}
                  accept={imageSection.accept || "image/*"}
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={(e) => handleImageUpload(e.target.files)}
                />

                <div className="main-preview">
                  {previewImages[0] ? (
                    <img src={previewImages[0]} alt={imageSection.mainAlt || "main"} />
                  ) : (
                    <div className="no-preview">{imageSection.uploadLabel}</div>
                  )}
                </div>

                <div className="thumb-preview">
                  {previewImages.slice(1, 6).map((img, i) => (
                    <img key={i} src={img} alt={imageSection.thumbAlt || "thumb"} />
                  ))}
                </div>

                <button
                  type="button"
                  className="add-more-btn"
                  onClick={() => fileInputRef.current.click()}
                >
                  {imageSection.addMoreLabel}
                </button>
              </div>

              <div className="form-section">
                <div className="form-header card-header">
                  <div className="header-badge">{pageTitle}</div>
                  <h2 className="form-title">{pageTitle}</h2>
                  <p className="form-subtitle">{pageSubtitle}</p>
                  {selectedRole && (
                    <div className="selected-role-badge">
                      {sellcar_page.roles.find((r) => r.value === selectedRole)?.name}
                    </div>
                  )}
                </div>

                <div className="form-grid">
                  {visibleFields.map((field) => (
                    <div
                      key={field.name}
                      className={`form-group ${field.layout === "full" ? "full-width" : ""} ${
                        fieldErrors[field.name] ? "has-error" : ""
                      }`}
                    >
                      <label className="field-label">
                        {field.label}
                        {field.required && <span className="required-mark">*</span>}
                      </label>
                      {renderField(field)}
                      {field.hint && !fieldErrors[field.name] && (
                        <span className="field-hint">{field.hint}</span>
                      )}
                      {fieldErrors[field.name] && (
                        <span className="field-error">{fieldErrors[field.name]}</span>
                      )}
                    </div>
                  ))}
                </div>

                {sellcar_page.map?.is_show !== false && (
                  <div className={`map-full-width ${fieldErrors.location ? "has-error" : ""}`}>
                    <LocationMap
                      config={sellcar_page.map}
                      value={selectedLocation}
                      onChange={handleLocationChange}
                    />
                    {fieldErrors.location && (
                      <span className="field-error">{fieldErrors.location}</span>
                    )}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    className="draft-btn"
                    onClick={handleDraft}
                    disabled={isSubmitting}
                  >
                    {actions.draftLabel}
                  </button>
                  <button className="submit-btn" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <CarLoader inline title={actions.submittingInline} />
                    ) : (
                      isEditing ? "Update Listing" : actions.submitLabel
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <PageLoadGate
      skeleton={
        <div className="sellcar">
          <FormPageSkeleton />
        </div>
      }
      delay={380}
    >
      {pageContent}
    </PageLoadGate>
  );
}
